import React from 'react';
import { Node } from '@contentful/rich-text-types';
import parse, {
  domToReact,
  Element,
  attributesToProps,
  DOMNode,
  HTMLReactParserOptions,
} from 'html-react-parser';

import Tick from '@src/components/Icons/Tick';
import Close from '@src/components/Icons/Close';

// Contentful only offers plain unordered/ordered lists, so unordered list variants are
// signalled with a lightweight text token at the start of a list item instead of a dedicated
// content type. The token is stripped from the rendered output.
const LIST_VARIANT_TOKENS = [
  { token: '[[list:tick]]', variant: 'tick' },
  { token: '[[list:cross]]', variant: 'cross' },
] as const;

type ListVariant = (typeof LIST_VARIANT_TOKENS)[number];

// Recursively find the first text value within a Contentful rich text node.
const getFirstTextValue = (node: any): string => {
  if (!node) return '';
  if (node.nodeType === 'text' && typeof node.value === 'string') return node.value;
  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      const value = getFirstTextValue(child);
      if (value) return value;
    }
  }
  return '';
};

// Match a variant from the leading text of an item.
const detectVariant = (text: string): ListVariant | null =>
  LIST_VARIANT_TOKENS.find(({ token }) => text.trimStart().startsWith(token)) ?? null;

// Detect a list variant from the leading text of a single Contentful list-item node, if any.
// Variants are resolved per item so a single list can freely mix ticks, crosses, and default bullets.
const getItemVariant = (itemNode: Node): ListVariant | null =>
  detectVariant(getFirstTextValue(itemNode));

// Remove the first occurrence of the variant token (plus any whitespace right after it) from the
// already-rendered list children, walking the React tree so inline marks stay intact.
const stripListToken = (children: React.ReactNode, token: string): React.ReactNode => {
  let done = false;

  const walk = (node: React.ReactNode): React.ReactNode => {
    if (done || node === null || node === undefined || typeof node === 'boolean') return node;

    if (typeof node === 'string') {
      const index = node.indexOf(token);
      if (index === -1) return node;
      done = true;
      return (node.slice(0, index) + node.slice(index + token.length)).replace(/^\s+/, '');
    }

    if (Array.isArray(node)) {
      return React.Children.map(node, walk);
    }

    if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
      const childChildren = node.props?.children;
      if (childChildren === undefined) return node;
      return React.cloneElement(node, undefined, walk(childChildren));
    }

    return node;
  };

  return walk(children);
};

export const VARIANT_MARKERS: Record<ListVariant['variant'], React.ReactNode> = {
  // Tick's path is hard-coded to fill white, so force the path to follow the text color instead.
  tick: <Tick className='text-body-copy--dark h-[1.1rem] w-auto [&_path]:fill-current' />,
  cross: <Close className='text-body-copy--dark h-[1.2rem] w-auto' />,
};

// Wrap a single list item: hide its native disc and place the marker in the list gutter so every
// marker (disc, tick or cross) shares one centre and the text stays aligned across the whole list.
const wrapListItem = (child: React.ReactNode, marker: React.ReactNode): React.ReactNode => {
  if (!React.isValidElement<{ children?: React.ReactNode; className?: string }>(child))
    return child;

  return React.cloneElement(
    child,
    { className: 'relative list-none' },
    <>
      {/* Spans the full gutter and centres the marker both axes. The box is exactly one line-height
          tall (`1lh`), so the marker stays centred on the first line regardless of the surrounding
          line-height (body list vs. table cell). */}
      <span
        className='absolute top-0 right-full left-[-2.85rem] flex h-lh w-[2.4rem] items-center justify-center'
        aria-hidden='true'
      >
        {marker}
      </span>
      {child.props?.children}
    </>
  );
};

// Apply per-item variants to a rendered unordered list: token-less items pass through untouched
// (keeping the native disc), variant items get their token stripped and a tick/cross marker. The
// `<ul>` wrapper and its styling stay with the renderer.
export const renderUnorderedListItems = (
  node: Node,
  children: React.ReactNode
): React.ReactNode => {
  const itemNodes = ((node as any)?.content ?? []) as Node[];

  return React.Children.map(children, (child, index) => {
    const variant = getItemVariant(itemNodes[index]);
    if (!variant) return child;
    return wrapListItem(stripListToken(child, variant.token), VARIANT_MARKERS[variant.variant]);
  });
};

// Concatenate the text content of parsed-HTML (domhandler) nodes.
const getDomText = (nodes: DOMNode[]): string =>
  nodes
    .map((node) => {
      if (node.type === 'text') return (node as any).data ?? '';
      if (node instanceof Element) return getDomText(node.children as DOMNode[]);
      return '';
    })
    .join('');

// Scan TinyMCE HTML and apply unordered-list variants: any <li> whose text starts with a token has
// the token stripped and gains a tick/cross marker, and its <ul> gets a left gutter so the marker
// has room. Everything else (token-less items, tables, other markup) is parsed as usual.
export const parseTinyMceWithVariants = (html?: string): React.ReactNode => {
  if (!html) return null;

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (!(domNode instanceof Element) || domNode.name !== 'ul') return undefined;

      let hasVariant = false;

      const items = (domNode.children as DOMNode[]).map((node, index) => {
        if (!(node instanceof Element) || node.name !== 'li') return domToReact([node], options);

        const li = <li key={index}>{domToReact(node.children as DOMNode[], options)}</li>;
        const variant = detectVariant(getDomText(node.children as DOMNode[]));
        if (!variant) return li;

        hasVariant = true;
        return wrapListItem(stripListToken(li, variant.token), VARIANT_MARKERS[variant.variant]);
      });

      const props = attributesToProps(domNode.attribs);
      const existingClass = typeof props.className === 'string' ? props.className : '';
      const className = hasVariant
        ? `${existingClass} relative`.trim()
        : existingClass || undefined;

      return (
        <ul {...props} className={className}>
          {items}
        </ul>
      );
    },
  };

  return parse(html, options);
};
